import { Component, OnDestroy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { LiveAssetNavigationService, LiveAssetsService, LiveAssetStatus, RoleService } from "@rvi/asset-modules";
import { TranslateService } from "@ngx-translate/core";
import { concatMap, filter, map, of, startWith, switchMap, take } from "rxjs";
import { LoginUserService } from "../_services/http-api/login-user.service";
import LiveAssetsStateService from "../_services/live-assets-state.service";
import { ConfirmationModalComponent } from "../_shared/component/confirmation-modal/confirmation-modal.component";
import { EvcModalService } from "../_shared/services/modal/evc-modal.service";
import { RoutePage } from "../app-routes.enum";
import { CreateInspectionModalComponent } from "../create-inspection-modal/create-inspection-modal.component";
import { AssetModuleClient } from "../_services/asset-module/asset-module-client.service";

type CreateInspectionNeededAction = "UpdateStatus" | "UpdateRequiredFields";

@Component({
  selector: "app-live-asset-page",
  templateUrl: "./live-asset-page.component.html",
  styleUrl: "./live-asset-page.component.scss",
  standalone: false
})
export class LiveAssetPageComponent implements OnDestroy {
  public canCreateAsset = false;
  public canCreateInspection = false;

  private getAssetIdFromParams = () => this.route.firstChild?.snapshot?.paramMap?.get("id") ?? null;

  private selectedAsset = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.getAssetIdFromParams()),
      filter((id) => !!id),
      switchMap((id) => this.assetModuleClient.getLiveAssetDetails(id))
    ),
    { initialValue: null }
  );

  constructor(
    public liveAssetManagementService: LiveAssetNavigationService,
    private liveAssetsStateService: LiveAssetsStateService,
    private assetModuleRoleService: RoleService,
    private router: Router,
    private route: ActivatedRoute,
    private evcModalService: EvcModalService,
    private assetModuleClient: AssetModuleClient,
    private loginUserService: LoginUserService,
    private translate: TranslateService
  ) {
    this.canCreateInspection = !this.loginUserService.isGuestRole();
    this.canCreateAsset = this.assetModuleRoleService.canI("CreateLiveAsset");
  }

  ngOnDestroy(): void {
    // refetch the new lives assets into the global state when exiting the page
    this.liveAssetsStateService.init().subscribe();
  }

  addLiveAsset() {
    this.liveAssetManagementService.goToCreateLiveAsset();
  }

  createInspection() {
    this.assetModuleClient
      .getLiveAssetDetails(this.selectedAsset().id)
      .pipe(
        concatMap((asset) => {
          if (asset.status !== LiveAssetStatus.Operational && asset.status !== LiveAssetStatus.OutOfService) {
            return this.assetModuleClient
              .validateStatus(asset.id, LiveAssetStatus.Operational)
              .pipe(map(({ isValid }) => (isValid ? "UpdateStatus" : "UpdateRequiredFields")));
          }
          return of(null);
        })
      )
      .subscribe((status: CreateInspectionNeededAction | null) => {
        switch (status) {
          case "UpdateStatus":
            return this.showStatusUpdateConfirmationModal();
          case "UpdateRequiredFields":
            return this.showMissingField();
          default:
            this.openCreateInspectionModal();
        }
      });
  }

  showMissingField() {
    const ref = this.evcModalService.showModal(ConfirmationModalComponent, {
      mainLabel: this.translate.instant("liveAssets.createInspection.missingFieldsTitle"),
      secondaryLabel: this.translate.instant("liveAssets.createInspection.missingFieldsMessage")
    });

    ref.componentRef.instance.confirm.pipe(take(1)).subscribe(() => {
      ref.close();
    });

    ref.componentRef.instance.reject.pipe(take(1)).subscribe(() => {
      ref.close();
    });
  }

  showStatusUpdateConfirmationModal() {
    const ref = this.evcModalService.showModal(ConfirmationModalComponent, {
      mainLabel: this.translate.instant("liveAssets.createInspection.statusUpdateConfirmation")
    });

    ref.componentRef.instance.confirm.pipe(take(1)).subscribe(() => {
      ref.close();
      this.assetModuleClient.updateLiveAssetStatus(this.selectedAsset().id, LiveAssetStatus.Operational, true).subscribe(() => {
        this.openCreateInspectionModal();
      });
    });

    ref.componentRef.instance.reject.pipe(take(1)).subscribe(() => {
      ref.close();
    });
  }

  openCreateInspectionModal() {
    const assetId = this.selectedAsset().id;
    if (!assetId) return;

    const modalRef = this.evcModalService.showModal(CreateInspectionModalComponent, { liveAssetId: assetId });

    modalRef.componentRef.instance.inspectionCreated.pipe(take(1)).subscribe((inspectionId) => {
      if (inspectionId) {
        this.router.navigate([RoutePage.InspectionDataManagement, "inspection", inspectionId]);
      }
    });
  }
}
